import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * Auth API using RTK Query
 *
 * Provides authentication endpoints with automatic caching,
 * request deduplication, and loading state management.
 *
 * Uses centralized baseQuery for consistent error handling.
 */
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  tagTypes: ['CurrentUser'],
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: () => '/api/v1/users/me/',
      providesTags: ['CurrentUser'],
    }),
    login: builder.mutation({
      query: (credentials) => ({
        url: '/api/v1/login/',
        method: 'POST',
        body: credentials,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformErrorResponse: (response) => {
        if (response.status === 401) {
          return { message: 'Invalid email or password' };
        }
        return { message: response.data?.error || 'Login failed' };
      },
    }),
    requestPasswordReset: builder.mutation({
      query: (body) => ({
        url: '/api/v1/password-reset/',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformErrorResponse: (response) => {
        return { message: response.data?.detail || 'Password reset request failed' };
      },
    }),
    validatePasswordResetToken: builder.mutation({
      query: (body) => ({
        url: '/api/v1/password-reset/validate_token/',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformErrorResponse: (response) => {
        if (response.status === 404) {
          return { message: 'Invalid reset token' };
        }
        if (response.status === 410) {
          return { message: 'Reset token has expired or was already used' };
        }
        return { message: response.data?.detail || 'Token validation failed' };
      },
    }),
    confirmPasswordReset: builder.mutation({
      query: (body) => ({
        url: '/api/v1/password-reset/confirm/',
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformErrorResponse: (response) => {
        if (response.status === 404) {
          return { message: 'Invalid reset token' };
        }
        if (response.status === 410) {
          return { message: 'Reset token has expired or was already used' };
        }
        return { message: response.data?.detail || 'Password reset failed' };
      },
    }),
    logout: builder.mutation({
      query: () => ({
        url: '/api/auth/logout/',
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useLoginMutation,
  useRequestPasswordResetMutation,
  useValidatePasswordResetTokenMutation,
  useConfirmPasswordResetMutation,
  useLogoutMutation,
} = authApi;
